import { Skeleton } from '@teamsnap/snap-ui';
import { Button, Field, Select } from '@teamsnap/teamsnap-ui';
import { AuthContext } from 'auth';
import {
  LoadingType,
  useRegistrationParticipants,
} from 'hooks/registrationParticipants';
import { ModalContainer } from '@teamsnap/snap-ui'
import * as React from 'react';

export type EmailType = 'individual' | 'all' | '';

type Props = {
  totalRegistrants: number;
  selectedRecordsLength: number;
  show: boolean;
  isSendingEmailToAllRegistrants: boolean;
  isSendingIndividualEmail: boolean;
  selectedIndividualEmail: string;
  onClose: () => any;
  onSubmit: (x: {
    sendFromEmail: string;
    subjectEmail: string;
    messageEmail: string;
  }) => Promise<boolean>;
  fromEmail: string;
};

const SendEmailModal = ({
  totalRegistrants,
  selectedRecordsLength,
  show,
  isSendingEmailToAllRegistrants,
  isSendingIndividualEmail,
  selectedIndividualEmail,
  onClose,
  onSubmit,
  fromEmail,
}: Props) => {
  const defaultFromEmail = fromEmail ? fromEmail : 'donotreply@teamsnap.com';
  const [subjectEmail, setSubjectEmail] = React.useState<string>('');
  const [messageEmail, setMessageEmail] = React.useState<string>('');
  const [sendFromEmail, setSendFromEmail] =
    React.useState<string>(defaultFromEmail);

  const context = React.useContext(AuthContext);

  const { loading } = useRegistrationParticipants();

  const { isSendEmailButtonDisabled, recipientLabel } = React.useMemo(() => {
    const isSendEmailButtonDisabled: boolean = (() => {
      if (isSendingEmailToAllRegistrants && totalRegistrants === 0) return true;
      if (
        !isSendingEmailToAllRegistrants &&
        !isSendingIndividualEmail &&
        selectedRecordsLength === 0
      )
        return true;
      if (isSendingIndividualEmail && !selectedIndividualEmail) return true;
      if (
        !isSendingIndividualEmail &&
        !isSendingEmailToAllRegistrants &&
        selectedRecordsLength === 0
      ) 
        return true;
      return false;
    })();

    const recipientLabel: string = (() => {
      if (isSendingIndividualEmail && selectedIndividualEmail)
        return selectedIndividualEmail;
      if (isSendingEmailToAllRegistrants) {
        return `${totalRegistrants} ${
          totalRegistrants === 1 ? 'Registrant' : 'Registrants'
        }`;
      }
      return `${selectedRecordsLength} ${
        selectedRecordsLength === 1 ? 'Registration' : 'Registrations'
      }`;
    })();

    return { isSendEmailButtonDisabled, recipientLabel };
  }, [
    isSendingEmailToAllRegistrants,
    totalRegistrants,
    isSendingIndividualEmail,
    selectedRecordsLength,
    selectedIndividualEmail,
  ]);

  const resetData = () => {
    setSendFromEmail(defaultFromEmail);
    setMessageEmail('');
    setSubjectEmail('');
  };

  const onSend = async () => {
    const ok = await onSubmit({ subjectEmail, messageEmail, sendFromEmail });

    if (ok) resetData();
  };

  const onCancel = () => {
    resetData();
    onClose();
  };

  return (
    <ModalContainer
      isOpen={show}
      onClose={onClose}
    >
      <div className="u-flex u-padMd u-textBold u-fontSizeXl u-borderBottom">
        {isSendingIndividualEmail ? 'Email Registrant' : 'Email Registrants'}
      </div>
      <div className="u-flex u-padBottomLg u-padTopMd" data-testid="send-email-modal-to">
        <div className="u-flex u-padLeftMd u-padRightMd u-textBold u-fontSizeMd">To:</div>
        <div className="u-flex u-fontSizeLg">
          {loading[LoadingType.GetUniqueRegistrants] ? (
            <Skeleton className="sui-w-4" />
          ) : (
            recipientLabel
          )}
        </div>
      </div>
      <div className="sui-flex sui-flex-col">
        {/* FROM: */}
        <div className={fromEmail ? 'u-flex u-padBottomLg' : 'u-padBottomLg'} data-testid="send-email-modal-from">
          <div className="u-flex u-padLeftMd u-padRightMd u-textBold u-fontSizeMd">From:</div>
          {fromEmail ? (
            <div className="u-flex u-fontSizeMd">{fromEmail}</div>
          ) : (
            <div className="u-padLeftMd u-padRightMd u-fontSizeMd">
              <Select
                name="Send Email from"
                options={[
                  {
                    label: context?.user?.email,
                    value: context?.user?.email,
                  },
                  {
                    label: 'donotreply@teamsnap.com',
                    value: 'donotreply@teamsnap.com',
                  },
                ]}
                inputProps={{
                  onChange: (e) => setSendFromEmail(e.target.value),
                  value: `${sendFromEmail}`,
                }}
              />
            </div>
          )}
        </div>
        {/* SUBJECT */}
        <div className="u-padBottomLg" data-testid="send-email-modal-subject">
          <div className="u-flex u-padLeftMd u-padRightMd u-textBold u-fontSizeMd">
            *Subject:
          </div>
          <div className="u-padLeftMd u-padRightMd">
            <Field
              type="input"
              formFieldProps={{
                size: 'large',
                placeholder: 'Email Subject',
                inputProps: {
                  value: subjectEmail,
                  onChange: (e) => setSubjectEmail(e.target.value),
                  maxLength: 255,
                },
              }}
              name="subject"
            />
          </div>
        </div>
        {/* MESSAGE */}
        <div className="u-padBottomLg" data-testid="send-email-modal-message">
          <div className="u-flex u-padLeftMd u-padRightMd u-textBold u-fontSizeMd">
            *Message:
          </div>
          <div className="u-padLeftMd u-padRightMd">
            <textarea
              style={{ height: 'auto' }}
              className="Input u-fontSizeMd"
              value={messageEmail}
              name="messageEmail"
              id="messageEmail"
              cols={30}
              rows={3}
              placeholder="Email Message"
              onChange={(e) => setMessageEmail(e.target.value)}
            />
          </div>
        </div>
      </div>
      {/* BUTTONS */}
      <div className="sui-flex sui-justify-end">
        <div className="buttons-wrapper">
          <Button mods="u-spaceRightSm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            mods="Button Button--primary"
            icon="mail"
            onClick={onSend}
            isDisabled={
              subjectEmail === '' ||
              messageEmail === '' ||
              isSendEmailButtonDisabled
            }
          >
            Send
          </Button>
        </div>
      </div>
    </ModalContainer>
  )
};

export default SendEmailModal;
